/**
 * CompetenceDetailSheet — bottom sheet with one competence's full detail:
 * name + state pill, can-do statement, correct/incorrect examples, learner
 * stats (productions / struggles / sessions / last used / next review),
 * contrastive difficulty + note + teaching tips when present, and the
 * "Practicar esto" primary action.
 *
 * The action goes through `useCompetenceFocusSession` (owned by the screen),
 * which carries the AI-consent gate — CM-020c's reason for extracting it was
 * exactly this second surface.
 *
 * Wraps the shared BottomSheetModal (safe-area-aware card).
 *
 * @see docs/specs/mobile/competence-map.mobile.md
 */

import React from 'react';
import { ActivityIndicator, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Ionicons from '../ui/Ionicons';
import { BottomSheetModal } from '../ui/BottomSheetModal';
import { colors, FS_XS, FS_SM, FS_MD } from '../../theme';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';
import type { CompetenceMapItem } from '../../generated/api/http-contracts';
import { translateErrorType } from '../practice/recommendedText';
import {
  showsExpectedHardBadge,
  translateStateLabel,
  type CompetenceState,
} from './competenceMapText';

interface CompetenceDetailSheetProps {
  competence: CompetenceMapItem;
  visible: boolean;
  onClose: () => void;
  /** Start a focused conversation session (spinner shown while creating). */
  onPractice: () => void;
  practiceLoading: boolean;
  /** Render inline without the Modal wrapper — for Storybook. */
  embedded?: boolean;
}

/** State pill colors — same semantic mapping as the chips. */
function useStatePillColors(state: CompetenceState) {
  const { theme } = useDisplayTheme();
  switch (state) {
    case 'mastered': return { bg: theme.successBg, text: theme.success };
    case 'learning': return { bg: theme.warningBg, text: theme.warning };
    case 'struggling': return { bg: theme.errorBg, text: theme.error };
    case 'untried': return { bg: theme.inputBg, text: theme.textSecondary };
    case 'upcoming': return { bg: theme.inputBg, text: theme.textSecondary };
    // Unknown future state: render neutrally rather than crash on
    // `pillColors.bg`. See CompetenceChip's `useStateColors`.
    default: return { bg: theme.inputBg, text: theme.textSecondary };
  }
}

function StatRow({ label, value }: { label: string; value: string }) {
  const { theme, fontScale, ffs } = useDisplayTheme();
  return (
    <View style={styles.statRow}>
      <Text
        allowFontScaling={false}
        style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }}
      >
        {label}
      </Text>
      <Text
        allowFontScaling={false}
        style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: theme.text, ...ffs }}
      >
        {value}
      </Text>
    </View>
  );
}

export function CompetenceDetailSheet({
  competence,
  visible,
  onClose,
  onPractice,
  practiceLoading,
  embedded = false,
}: CompetenceDetailSheetProps) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();
  const pillColors = useStatePillColors(competence.state);

  const observed = competence.state !== 'untried' && competence.state !== 'upcoming';
  const hasTips = competence.teachingTips.length > 0;
  const name = translateErrorType(t, competence.slug);

  return (
    <BottomSheetModal
      visible={visible}
      onRequestClose={onClose}
      embedded={embedded}
      testID="competence-map-detail"
    >
      <ScrollView showsVerticalScrollIndicator={false}>
        {/* ── Header: name + state pill + close ── */}
        <View style={styles.headerRow}>
          <View style={styles.headerText}>
            <Text
              allowFontScaling={false}
              accessibilityRole="header"
              style={{ fontSize: FS_MD * fontScale, fontWeight: '700', color: theme.text, ...ffs }}
            >
              {name}
            </Text>
            <View style={[styles.statePill, { backgroundColor: pillColors.bg }]}>
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_XS * fontScale, fontWeight: '600', color: pillColors.text, ...ffs }}
              >
                {translateStateLabel(t, competence.state)}
              </Text>
            </View>
          </View>
          <TouchableOpacity
            testID="competence-map-detail-close"
            accessibilityRole="button"
            accessibilityLabel={t('competenceMap.detail.closeA11y')}
            onPress={onClose}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Ionicons name="close" size={24 * fontScale} color={theme.textSecondary} />
          </TouchableOpacity>
        </View>

        {/* ── Can-do statement ── */}
        {!!competence.canDoStatement && (
          <View style={styles.section}>
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_SM * fontScale, color: theme.text, ...ffs }}
            >
              {competence.canDoStatement}
            </Text>
          </View>
        )}

        {/* ── Examples ── */}
        {competence.examples.length > 0 && (
          <View style={styles.section}>
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_XS * fontScale, fontWeight: '600', color: theme.textSecondary, ...ffs }}
            >
              {t('competenceMap.detail.examplesTitle')}
            </Text>
            {competence.examples.map((example, index) => (
              <View key={index} style={styles.exampleRow}>
                <Text
                  allowFontScaling={false}
                  style={{ fontSize: FS_SM * fontScale, color: theme.success, ...ffs }}
                >
                  {t('competenceMap.detail.exampleCorrect', { text: example.correct })}
                </Text>
                {!!example.incorrect && (
                  <Text
                    allowFontScaling={false}
                    style={{ fontSize: FS_SM * fontScale, color: theme.error, marginTop: 2, ...ffs }}
                  >
                    {t('competenceMap.detail.exampleIncorrect', { text: example.incorrect })}
                  </Text>
                )}
              </View>
            ))}
          </View>
        )}

        {/* ── Learner stats ── */}
        <View style={[styles.statsCard, { backgroundColor: theme.inputBg }]}>
          {observed ? (
            <>
              <StatRow
                label={t('competenceMap.detail.statsProductions')}
                value={String(competence.productions)}
              />
              <StatRow
                label={t('competenceMap.detail.statsStruggles')}
                value={String(competence.struggles)}
              />
              <StatRow
                label={t('competenceMap.detail.statsSessions')}
                value={String(competence.sessionsCount)}
              />
              {competence.lastUsedAt !== null && (
                <StatRow
                  label={t('competenceMap.detail.statsLastUsed')}
                  value={new Date(competence.lastUsedAt).toLocaleDateString()}
                />
              )}
              {competence.dueAt !== null && (
                <StatRow
                  label={t('competenceMap.detail.statsNextReview')}
                  value={new Date(competence.dueAt).toLocaleDateString()}
                />
              )}
            </>
          ) : (
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }}
            >
              {competence.state === 'upcoming'
                ? t('competenceMap.detail.comesLater')
                : t('competenceMap.detail.notSeenYet')}
            </Text>
          )}
        </View>

        {/* ── Contrastive difficulty ── */}
        {competence.difficulty !== null && (
          <View style={styles.section}>
            <View style={styles.statRow}>
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }}
              >
                {t('competenceMap.detail.difficultyLabel')}
              </Text>
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: theme.text, ...ffs }}
              >
                {t('competenceMap.detail.difficultyValue', { difficulty: competence.difficulty })}
              </Text>
            </View>
            {showsExpectedHardBadge(competence) && (
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_XS * fontScale, color: theme.warning, marginTop: 4, ...ffs }}
              >
                {t('competenceMap.detail.expectedHard')}
              </Text>
            )}
            {!!competence.contrastiveNote && (
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_SM * fontScale, color: theme.text, marginTop: 6, ...ffs }}
              >
                {competence.contrastiveNote}
              </Text>
            )}
          </View>
        )}

        {/* ── Teaching tips ── */}
        {hasTips && (
          <View style={styles.section}>
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_XS * fontScale, fontWeight: '600', color: theme.textSecondary, ...ffs }}
            >
              {t('competenceMap.detail.tipsTitle')}
            </Text>
            {competence.teachingTips.map((tip, index) => (
              <View key={index} style={styles.tipRow}>
                <Text
                  allowFontScaling={false}
                  accessible={false}
                  importantForAccessibility="no"
                  style={{ fontSize: FS_SM * fontScale, color: theme.primary, ...ffs }}
                >
                  •
                </Text>
                <Text
                  allowFontScaling={false}
                  style={{ flex: 1, fontSize: FS_SM * fontScale, color: theme.text, marginLeft: 8, ...ffs }}
                >
                  {tip}
                </Text>
              </View>
            ))}
          </View>
        )}

        {/* ── Primary action ── */}
        <TouchableOpacity
          testID="competence-map-detail-practice"
          accessibilityRole="button"
          accessibilityLabel={t('competenceMap.detail.practiceButton')}
          accessibilityState={{ disabled: practiceLoading }}
          onPress={onPractice}
          disabled={practiceLoading}
          activeOpacity={0.8}
          style={[styles.practiceButton, { backgroundColor: colors.primary, opacity: practiceLoading ? 0.7 : 1 }]}
        >
          {practiceLoading ? (
            <ActivityIndicator size="small" color="#FFFFFF" />
          ) : (
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_MD * fontScale, fontWeight: '700', color: '#FFFFFF', ...ffs }}
            >
              {t('competenceMap.detail.practiceButton')}
            </Text>
          )}
        </TouchableOpacity>
      </ScrollView>
    </BottomSheetModal>
  );
}

const styles = StyleSheet.create({
  headerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  headerText: {
    flex: 1,
    marginRight: 12,
  },
  statePill: {
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
    marginTop: 6,
  },
  section: {
    marginTop: 16,
  },
  exampleRow: {
    marginTop: 8,
  },
  statsCard: {
    marginTop: 16,
    borderRadius: 12,
    padding: 12,
    gap: 8,
  },
  statRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  tipRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: 6,
  },
  practiceButton: {
    marginTop: 24,
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
  },
});
